import { usePlanner } from "@/features/space-designer/lib/store";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  MousePointer2,
  Minus,
  Ruler,
  Square,
  Hand,
  Eraser,
  Undo2,
  Redo2,
  Trash2,
  Download,
  Upload,
  Copy,
  RotateCw,
  Magnet,
  ZoomIn,
  ZoomOut,
  LocateFixed,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Cloud } from "lucide-react";
import { Link } from "wouter";

export function Toolbar() {
  const {
    tool, setTool, undo, redo, clearAll, unit, setUnit,
    duplicateSelected, removeSelected, rotateSelected,
    snap, toggleSnap, zoom, setZoom, setPan, scene, loadScene,
    history, future, selectedIds,
  } = usePlanner();

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") { e.preventDefault(); duplicateSelected(); }
      else if (e.key === "Delete" || e.key === "Backspace") { if (selectedIds.length) { e.preventDefault(); removeSelected(); } }
      else if (e.key === "r" || e.key === "R") { rotateSelected(90); }
      else if (e.key === "v" || e.key === "V") setTool("select");
      else if (e.key === "w" || e.key === "W") setTool("wall");
      else if (e.key === "m" || e.key === "M") setTool("measure");
      else if (e.key === "a" || e.key === "A") setTool("area");
      else if (e.key === "h" || e.key === "H") setTool("pan");
      else if (e.key === "e" || e.key === "E") setTool("erase");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, duplicateSelected, removeSelected, rotateSelected, setTool, selectedIds]);

  function exportJson() {
    const blob = new Blob([JSON.stringify(scene, null, 2)], { type: "application/json" });
    triggerDownload(blob, "floorplan.json");
  }

  async function exportPng() {
    const svg = document.getElementById("floor-planner-svg") as SVGSVGElement | null;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("width", String(rect.width));
    clone.setAttribute("height", String(rect.height));
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fcfcfa";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => {
        if (b) triggerDownload(b, "floorplan.png");
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.src = url;
  }

  function onImport(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (parsed.items && parsed.walls) loadScene(parsed);
      } catch {}
    };
    reader.readAsText(f);
    e.target.value = "";
  }

  function fitPlanToView() {
    const bounds = getSceneBounds(scene);
    const svg = document.getElementById("floor-planner-svg") as SVGSVGElement | null;
    const rect = svg?.getBoundingClientRect();
    if (!bounds || !rect?.width || !rect?.height) {
      setZoom(1);
      setPan({ x: 200, y: 150 });
      return;
    }

    const padding = 80;
    const contentW = Math.max(1, bounds.maxX - bounds.minX);
    const contentH = Math.max(1, bounds.maxY - bounds.minY);
    const nextZoom = Math.max(0.2, Math.min(2.5, Math.min((rect.width - padding * 2) / contentW, (rect.height - padding * 2) / contentH)));
    setZoom(nextZoom);
    setPan({
      x: rect.width / 2 - ((bounds.minX + bounds.maxX) / 2) * nextZoom,
      y: rect.height / 2 - ((bounds.minY + bounds.maxY) / 2) * nextZoom,
    });
  }

  const tools: { id: typeof tool; icon: React.ReactNode; label: string; key: string }[] = [
    { id: "select", icon: <MousePointer2 className="w-4 h-4" />, label: "Select", key: "V" },
    { id: "wall", icon: <Minus className="w-4 h-4" />, label: "Wall", key: "W" },
    { id: "measure", icon: <Ruler className="w-4 h-4" />, label: "Measure", key: "M" },
    { id: "area", icon: <Square className="w-4 h-4" />, label: "Area", key: "A" },
    { id: "erase", icon: <Eraser className="w-4 h-4" />, label: "Eraser", key: "E" },
    { id: "pan", icon: <Hand className="w-4 h-4" />, label: "Pan", key: "H" },
  ];

  return (
    <div className="h-12 border-b border-border bg-card flex items-center px-3 gap-1">
      <div className="font-semibold text-sm pr-3 text-foreground/90">Floor Planner</div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-1 px-2">
        {tools.map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant={tool === t.id ? "default" : "ghost"}
            className="h-8 px-2 gap-1"
            onClick={() => setTool(t.id)}
            title={`${t.label} (${t.key})`}
          >
            {t.icon}
            <span className="text-xs">{t.label}</span>
          </Button>
        ))}
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-1 px-2">
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={undo} disabled={!history.length} title="Undo (Ctrl+Z)">
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={redo} disabled={!future.length} title="Redo (Ctrl+Shift+Z)">
          <Redo2 className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={duplicateSelected} disabled={!selectedIds.length} title="Duplicate (Ctrl+D)">
          <Copy className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => rotateSelected(90)} disabled={!selectedIds.length} title="Rotate 90° (R)">
          <RotateCw className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={removeSelected} disabled={!selectedIds.length} title="Delete (Del)">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <Separator orientation="vertical" className="h-6" />
      <div className="flex items-center gap-1 px-2">
        <Button
          size="sm"
          variant={snap ? "default" : "ghost"}
          className="h-8 px-2 gap-1"
          onClick={toggleSnap}
          title="Snap to grid"
        >
          <Magnet className="w-4 h-4" />
          <span className="text-xs">Snap</span>
        </Button>
        <div className="flex border border-border rounded-md overflow-hidden">
          {(["cm", "m", "ft"] as const).map((u) => (
            <button
              key={u}
              onClick={() => setUnit(u)}
              className={`text-xs px-2 h-7 ${unit === u ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-accent"}`}
            >
              {u}
            </button>
          ))}
        </div>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setZoom(zoom * 0.9)} title="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <div className="text-xs text-muted-foreground w-12 text-center">{Math.round(zoom * 100)}%</div>
        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setZoom(zoom * 1.1)} title="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2 gap-1" onClick={fitPlanToView} title="Find / fit plan in view">
          <LocateFixed className="w-4 h-4" />
          <span className="text-xs">Fit</span>
        </Button>
      </div>
      <div className="ml-auto flex items-center gap-1">
        <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={onImport} />
        <Button asChild size="sm" variant="ghost" className="h-8 px-2 gap-1" title="Cloud SVG library">
          <Link href="/inventory/space/designer/svg-library">
            <Cloud className="w-4 h-4" /> <span className="text-xs">Cloud</span>
          </Link>
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2 gap-1" onClick={() => fileRef.current?.click()}>
          <Upload className="w-4 h-4" /> <span className="text-xs">Import</span>
        </Button>
        <Button size="sm" variant="ghost" className="h-8 px-2 gap-1" onClick={exportJson}>
          <Download className="w-4 h-4" /> <span className="text-xs">JSON</span>
        </Button>
        <Button size="sm" variant="default" className="h-8 px-2 gap-1" onClick={exportPng}>
          <Download className="w-4 h-4" /> <span className="text-xs">PNG</span>
        </Button>
        <Separator orientation="vertical" className="h-6 mx-1" />
        <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => { if (confirm("Clear the whole plan?")) clearAll(); }}>
          Clear
        </Button>
      </div>
    </div>
  );
}

function getSceneBounds(scene: ReturnType<typeof usePlanner.getState>["scene"]) {
  const boxes: { minX: number; minY: number; maxX: number; maxY: number }[] = [];
  scene.items.forEach((it) => boxes.push({ minX: it.x, minY: it.y, maxX: it.x + it.w, maxY: it.y + it.h }));
  scene.walls.forEach((w) => boxes.push({ minX: Math.min(w.x1, w.x2), minY: Math.min(w.y1, w.y2), maxX: Math.max(w.x1, w.x2), maxY: Math.max(w.y1, w.y2) }));
  scene.measurements.forEach((m) => boxes.push({ minX: Math.min(m.x1, m.x2), minY: Math.min(m.y1, m.y2), maxX: Math.max(m.x1, m.x2), maxY: Math.max(m.y1, m.y2) }));
  scene.areas.forEach((a) => {
    if (!a.points.length) return;
    boxes.push({
      minX: Math.min(...a.points.map((p) => p.x)),
      minY: Math.min(...a.points.map((p) => p.y)),
      maxX: Math.max(...a.points.map((p) => p.x)),
      maxY: Math.max(...a.points.map((p) => p.y)),
    });
  });
  if (scene.blueprint) boxes.push({ minX: scene.blueprint.x, minY: scene.blueprint.y, maxX: scene.blueprint.x + scene.blueprint.w, maxY: scene.blueprint.y + scene.blueprint.h });
  if (!boxes.length) return null;
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

function triggerDownload(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}